import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ChevronRight, CreditCard, Calendar, Users, MapPin, AlertCircle, Shield, FileText, Loader2, Check } from "lucide-react";
import { BackButton } from "@/components/ui/back-button";
import { useToast } from "@/hooks/use-toast";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { LazyPrivacyPolicyModal, LazyRefundPolicyModal } from "@/components/LazyModals";
import { loadTossPayments } from "@tosspayments/payment-sdk";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import { useBookingForPayment, useUpdateBookingForPayment, BookingParticipant as PaymentParticipant } from "@/hooks/usePaymentHooks";
import { BOOKING_STATUS } from "@/lib/booking-status";
interface BookingParticipant {
  name: string;
  phone: string;
  seat?: number | null;
}
interface BookingData {
  bookingId: number;
  userId: string;
  selectedDate: string;
  totalPrice: number;
  participants: BookingParticipant[];
  product: {
    name: string;
  };
  trip: {
    departure_point?: string | null;
  };
}
const Payment = () => {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    toast
  } = useToast();

  // 컴포넌트 마운트 시 스크롤을 최상단으로 이동
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const {
    user
  } = useAuth();
  const [agreements, setAgreements] = useState({
    all: false,
    privacy: false,
    refund: false
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const isMobile = useIsMobile();

  // React Query hooks
  const {
    data: bookingResponse,
    isLoading,
    error: bookingError
  } = useBookingForPayment(id || '', user?.id);
  
  const updateBookingMutation = useUpdateBookingForPayment();

  // TossPayments client key should be set in your .env file
  // VITE_TOSSPAYMENTS_CLIENT_KEY=test_ck_your_key_here
  const clientKey = import.meta.env.VITE_TOSSPAYMENTS_CLIENT_KEY;
  if (!clientKey) {
    console.error('Missing TossPayments client key. Please check your .env file.');
  }

  // Transform booking data to match existing BookingData interface
  const bookingData: BookingData | null = bookingResponse ? {
    bookingId: bookingResponse.booking_id,
    userId: bookingResponse.user_id,
    selectedDate: bookingResponse.trips.departure_date,
    totalPrice: bookingResponse.total_price,
    participants: bookingResponse.participants,
    product: {
      name: bookingResponse.trips.products.name
    },
    trip: {
      departure_point: bookingResponse.trips.departure_point
    }
  } : null;

  // Handle query states and validations
  useEffect(() => {
    // user나 id가 없으면 에러 처리 후 종료
    if (!user || !id) {
      toast({
        title: "접근 오류",
        description: "올바르지 않은 접근입니다.",
        variant: "destructive"
      });
      navigate("/");
      return;
    }

    // 에러 처리
    if (bookingError) {
      console.error('Error fetching booking data:', bookingError);
      if (bookingError instanceof Error) {
        if (bookingError.name === 'AbortError') {
          toast({
            title: "요청 시간 초과",
            description: "예약 정보를 불러오는 데 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.",
            variant: "destructive"
          });
        } else if (bookingError.message.includes('접근 권한')) {
          toast({
            title: "접근 권한 없음",
            description: "본인의 예약만 결제할 수 있습니다.",
            variant: "destructive"
          });
        } else if (bookingError.message.includes('예약 정보를 찾을 수 없습니다')) {
          toast({
            title: "예약 정보 없음",
            description: "예약 정보를 찾을 수 없습니다.",
            variant: "destructive"
          });
        } else {
          toast({
            title: "오류 발생",
            description: "예약 정보를 불러오는 중 오류가 발생했습니다.",
            variant: "destructive"
          });
        }
      }
      navigate("/");
      return;
    }

    // 이미 결제된 예약인지 확인
    if (bookingResponse && bookingResponse.status === BOOKING_STATUS.CONFIRMED) {
      toast({
        title: "이미 결제 완료",
        description: "이미 결제가 완료된 예약입니다.",
        variant: "destructive"
      });
      navigate(`/booking-success/${id}`);
      return;
    }
  }, [user, id, bookingError, bookingResponse, navigate, toast]);

  // AuthProvider에서 전역적으로 탭 전환 문제를 해결하므로 로컬 솔루션 제거됨
  const handleAgreementChange = (type: string, checked: boolean) => {
    if (type === "all") {
      setAgreements({
        all: checked,
        privacy: checked,
        refund: checked
      });
    } else {
      const newAgreements = {
        ...agreements,
        [type]: checked
      };
      newAgreements.all = newAgreements.privacy && newAgreements.refund;
      setAgreements(newAgreements);
    }
  };

  // 예약 상세 정보 렌더링 함수
  const renderBookingDetails = () => {
    if (!bookingData) return null;
    
    return <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500 mb-1">출발일</p>
              <p className="font-medium flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                {format(new Date(bookingData.selectedDate), 'yyyy/MM/dd', {
                locale: ko
              })}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">출발지</p>
              <p className="font-medium flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-400" />
                {bookingData.trip.departure_point || '출발지 미정'}
              </p>
            </div>
          </div>
          
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500 mb-1">참가 인원</p>
              <p className="font-medium flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-400" />
                {bookingData.participants.length}명
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">선택 좌석</p>
              <div className="flex flex-wrap gap-1">
                {bookingData.participants.map((participant, index) => participant.seat && <Badge key={`seat-${participant.seat}-${index}`} variant="secondary" className="text-xs">
                      {participant.seat}번
                    </Badge>)}
              </div>
            </div>
          </div>
        </div>
        
        <Separator />
        
        <div>
          <p className="text-sm font-medium mb-2">참가자 정보</p>
          <div className="space-y-2">
            {bookingData.participants.map((participant, index) => <div key={`participant-${participant.name}-${index}`} className="flex items-center justify-between text-sm">
                <span className="text-gray-600">
                  {index === 0 ? '대표자' : `참가자 ${index + 1}`}
                </span>
                <span>
                  {participant.name} ({participant.phone})
                  {participant.seat && <Badge variant="outline" className="ml-2 text-xs">
                      좌석 {participant.seat}
                    </Badge>}
                </span>
              </div>)}
          </div>
        </div>
      </>;
  };
  const handlePayment = async () => {
    if (!agreements.privacy || !agreements.refund) {
      toast({
        title: "약관 동의 필요",
        description: "필수 약관에 동의해주세요.",
        variant: "destructive"
      });
      return;
    }

    // 추가 보안 검증
    if (!user) {
      toast({
        title: "로그인 필요",
        description: "결제를 진행하려면 로그인이 필요합니다.",
        variant: "destructive"
      });
      navigate('/auth', {
        replace: true
      });
      return;
    }
    if (!bookingData || !id) {
      toast({
        title: "결제 정보 부족",
        description: "결제를 진행할 수 없습니다. 예약 정보를 확인해주세요.",
        variant: "destructive"
      });
      return;
    }

    // 예약 소유자 재확인
    if (bookingData.userId !== user.id) {
      toast({
        title: "권한 없음",
        description: "본인의 예약만 결제할 수 있습니다.",
        variant: "destructive"
      });
      navigate('/', {
        replace: true
      });
      return;
    }
    if (!clientKey) {
      toast({
        title: "결제 설정 오류",
        description: "결제 시스템 설정에 문제가 있습니다. 관리자에게 문의해주세요.",
        variant: "destructive"
      });
      return;
    }
    setIsProcessing(true);
    const orderId = `${user.id.substring(0, 8)}_${Date.now()}`;
    try {
      // 1. Update existing booking status and order_id using React Query mutation
      const updateResult = await updateBookingMutation.mutateAsync({
        bookingId: bookingData.bookingId,
        orderId: orderId,
        status: BOOKING_STATUS.PENDING
      });
      
      if (updateResult.error) {
        throw new Error(`예약 업데이트 실패: ${updateResult.error.message}`);
      }

      // 2. Proceed to Toss Payments
      const tossPayments = await loadTossPayments(clientKey);
      await tossPayments.requestPayment("카드", {
        amount: bookingData.totalPrice,
        orderId: orderId,
        orderName: bookingData.product.name,
        customerName: user.user_metadata.name || user.email,
        successUrl: `${window.location.origin}/booking-success/${id}`,
        failUrl: `${window.location.origin}/payment-fail`
      });
    } catch (error: unknown) {
      console.error("Payment Error:", error);
      toast({
        title: "결제 오류",
        description: error instanceof Error ? error.message : "결제 중 오류가 발생했습니다. 다시 시도해주세요.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // 로딩 중일 때 표시
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">예약 정보를 불러오는 중...</p>
        </div>
      </div>;
  }

  // 예약 정보가 없는 경우 (정상적으로는 이미 redirect 되어야 함)
  if (!bookingData) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 text-lg mb-4">예약 정보를 찾을 수 없습니다.</p>
          <Button onClick={() => navigate('/')} variant="outline" type="button">
            홈으로 이동
          </Button>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-gray-50">
      {/* 헤더 - 스크롤 상단 고정 */}
      <div className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-center relative">
            <BackButton onClick={() => navigate(-1)} className="absolute left-0">
              뒤로
            </BackButton>
            <h1 className="text-lg font-bold text-gray-800">결제하기</h1>
          </div>
        </div>
      </div>
      
      <div className="bg-white border-b shadow-sm">
        <div className="container mx-auto px-4 py-4">
          {/* 진행 단계 표시 */}
          <div className="mb-4">
            <div className="flex items-center justify-center space-x-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gray-300 text-gray-500 rounded-full flex items-center justify-center text-sm">
                  <Check className="w-4 h-4" />
                </div>
                <span className="ml-2 text-gray-500 text-sm">예약 정보</span>
              </div>
              <div className="w-12 h-1 bg-gray-300"></div>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-[#F39800] text-white rounded-full flex items-center justify-center text-sm font-semibold">2</div>
                <span className="ml-2 text-[#F39800] font-semibold text-sm">결제하기</span>
              </div>
              <div className="w-12 h-1 bg-gray-300"></div>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gray-300 text-gray-500 rounded-full flex items-center justify-center text-sm">3</div>
                <span className="ml-2 text-gray-500 text-sm">예약 완료</span>
              </div>
            </div>
          </div>
          
          <h2 className="text-xl md:text-2xl font-bold text-gray-800">{bookingData.product.name}</h2>
          <p className="text-sm text-gray-600 mt-1">
            {format(new Date(bookingData.selectedDate), 'yyyy/MM/dd', {
            locale: ko
          })} 출발
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 lg:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 결제 정보 영역 */}
          <div className="lg:col-span-2 space-y-4">
            {/* 예약 상세 정보 */}
            {isMobile ? <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
                <Card className="shadow-sm">
                  <CollapsibleTrigger className="w-full">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-[#F39800]" />
                          예약 상세 정보
                        </CardTitle>
                        <ChevronRight className={cn("w-5 h-5 text-gray-400 transition-transform", detailsOpen && "rotate-90")} />
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="space-y-4 pt-0">
                      {renderBookingDetails()}
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible> : <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-[#F39800]" />
                    예약 상세 정보
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {renderBookingDetails()}
                </CardContent>
              </Card>}

            {/* 결제 방법 선택 */}
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle className="text-base md:text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#F39800]" />
                  결제 방법
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="p-4 border-2 border-[#F39800] rounded-lg bg-[#F39800]/5">
                  <div className="flex items-center space-x-3">
                    <Checkbox checked={true} disabled className="data-[state=checked]:bg-[#F39800] data-[state=checked]:border-[#F39800]" />
                    <div className="flex-1">
                      <Label className="font-medium">신용/체크카드</Label>
                      <p className="text-xs text-gray-600 mt-1">모든 카드사 결제 가능</p>
                    </div>
                    <CreditCard className="w-6 h-6 text-[#F39800]" />
                  </div>
                </div>
                
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800 flex items-start gap-2">
                    <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      토스페이먼츠의 안전한 결제 시스템으로 보호됩니다.
                      카드 정보는 암호화되어 안전하게 처리됩니다.
                    </span>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* 약관 동의 */}
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle className="text-base md:text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#F39800]" />
                  약관 동의
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Checkbox checked={agreements.all} onCheckedChange={checked => handleAgreementChange('all', checked as boolean)} className="data-[state=checked]:bg-[#F39800] data-[state=checked]:border-[#F39800]" />
                      <Label className="font-medium cursor-pointer">전체 동의</Label>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 hover:bg-gray-50 rounded transition-colors">
                      <div className="flex items-center space-x-3">
                        <Checkbox checked={agreements.privacy} onCheckedChange={checked => handleAgreementChange('privacy', checked as boolean)} className="data-[state=checked]:bg-[#F39800] data-[state=checked]:border-[#F39800]" />
                        <Label className="text-sm cursor-pointer">
                          <span className="text-red-500">*</span> 개인정보 제3자 제공 동의 (필수)
                        </Label>
                      </div>
                      <LazyPrivacyPolicyModal>
                        <button type="button" className="text-gray-400 hover:text-gray-600">
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </LazyPrivacyPolicyModal>
                    </div>
                    
                    <div className="flex items-center justify-between p-2 hover:bg-gray-50 rounded transition-colors">
                      <div className="flex items-center space-x-3">
                        <Checkbox checked={agreements.refund} onCheckedChange={checked => handleAgreementChange('refund', checked as boolean)} className="data-[state=checked]:bg-[#F39800] data-[state=checked]:border-[#F39800]" />
                        <Label className="text-sm cursor-pointer">
                          <span className="text-red-500">*</span> 취소 및 환불 규정 동의 (필수)
                        </Label>
                      </div>
                      <LazyRefundPolicyModal>
                        <button type="button" className="text-gray-400 hover:text-gray-600">
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </LazyRefundPolicyModal>
                    </div>
                  </div>
                </div>
                
                {(!agreements.privacy || !agreements.refund) && <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="text-sm text-amber-800 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      필수 약관에 모두 동의해주세요.
                    </p>
                  </div>}
              </CardContent>
            </Card>
          </div>

          {/* 결제 요약 영역 */}
          <div className="lg:col-span-1">
            <Card className="shadow-sm sticky top-20 mx-2 lg:mx-0">
              <CardHeader className="bg-gray-50">
                <CardTitle className="text-lg">결제 정보</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">상품 금액</span>
                    <span>{bookingData.totalPrice.toLocaleString()}원</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">인원</span>
                    <span>{bookingData.participants.length}명</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between font-bold text-lg">
                    <span>총 결제금액</span>
                    <span className="text-[#F39800]">{bookingData.totalPrice.toLocaleString()}원</span>
                  </div>
                </div>

                <div className="space-y-2 text-xs text-gray-600">
                  <div className="flex items-start gap-1">
                    <span>•</span>
                    <span>결제 완료 후 예약이 확정됩니다</span>
                  </div>
                  <div className="flex items-start gap-1">
                    <span>•</span>
                    <span>출발 4일 전까지 무료 취소 가능</span>
                  </div>
                  <div className="flex items-start gap-1">
                    <span>•</span>
                    <span>결제 내역은 이메일로 발송됩니다</span>
                  </div>
                </div>
                
                {/* 데스크톱 결제 버튼 - 총 결제금액 하단에 위치 */}
                {!isMobile && (
                  <Button 
                    onClick={handlePayment} 
                    className="w-full bg-[#F39800] hover:bg-[#E08700] text-white"
                    size="lg"
                    disabled={!bookingData || isProcessing || !agreements.privacy || !agreements.refund}
                  >
                    {isProcessing ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        처리 중...
                      </span>
                    ) : (
                      '결제하기'
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* 모바일 하단 고정 결제 버튼 */}
      {isMobile && <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-50">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-gray-600">총 결제금액</span>
            <span className="text-xl font-bold text-[#F39800]">
              {bookingData.totalPrice.toLocaleString()}원
            </span>
          </div>
          <Button onClick={handlePayment} className="w-full h-12 bg-[#F39800] hover:bg-[#E08700] text-white font-bold text-lg" disabled={!bookingData || isProcessing || !agreements.privacy || !agreements.refund}>
            {isProcessing ? <span className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                처리 중...
              </span> : '결제하기'}
          </Button>
        </div>}


      <Footer />
    </div>;
};
export default Payment;